/**
 * The pose surface's bone vocabulary — ONE home for the drawn/hidden rule
 * (ADR-006 §8's one-source-of-truth discipline: the visual and the walker
 * MUST agree on which bones a person poses, and a second copy of this rule
 * is exactly how they'd drift apart).
 *
 * **The mapping decides, not the name (#70).** `*Twist*` is the NAME SHAPE of
 * rig plumbing — the retarget's substrate, derived not posed
 * (PRODUCT_TARGET §mechanisms) — but a name is a hint and the mapper's
 * verdict is evidence. On the Tripo family `NeckTwist01` IS the neck: the
 * lexicon maps it (`Neck: 'NeckTwist01'`), it carries 32 of the specimen
 * clip's 704 keys, and it measures ~6.6° of head motion. Reading its NAME in
 * place of that verdict hid a real, animated, mapped joint from drawing,
 * picking and walking on every skeleton this app has ever drawn. So a
 * `*Twist*` bone the mapper CLAIMED is vocabulary; one it left unmapped is
 * plumbing. `NeckTwist02` and the limb twists stay hidden by that same rule,
 * because the mapper genuinely left them out — the rig's own evidence, not a
 * hand-kept exception list.
 *
 * `*_corr` bones are ADR-028 corrective helpers — DERIVED by the hinge driver
 * from the bones a person actually poses; a hand-authored pose on one would be
 * overwritten on the next frame, so the surface never offers it and no mapping
 * can redeem it.
 *
 * Hidden bones remain fully addressable through the agent register; this is a
 * VISUAL vocabulary, not a schema.
 * Engine-free (session-no-engine). Depends on: nothing.
 * Depended on by: viewport/bone-visual (drawing), session/pick-walker
 * (traversal), main (the keymap's guard + the pose-target gate).
 */

/**
 * Derived correctives — never posable, whatever the mapper says. Unlike the
 * plumbing rule below this one is unconditional: these bones are WRITTEN every
 * frame by the hinge driver, so posing one is a contradiction, not a choice.
 */
export const DERIVED_BONES = /_corr$/;

/**
 * The NAME SHAPE of rig plumbing. Never consult this alone — it is the hint
 * that must lose to the mapper's verdict (see the header, and #70).
 */
export const PLUMBING_NAME = /Twist/;

/**
 * Is this bone part of the pose surface's visible/walkable vocabulary?
 *
 * `mapped` is the rig's own mapped-bone set — build it with `mappedRigBones`.
 * It is REQUIRED rather than optional on purpose: an omitted mapping would
 * silently restore the name-only behaviour this function exists to end, and a
 * silent fallback to the old wrong answer is precisely the failure this
 * codebase refuses (LAWS.md — absence doesn't throw).
 */
export function isPosableBone(name: string, mapped: ReadonlySet<string>): boolean {
  if (DERIVED_BONES.test(name)) return false;
  if (!PLUMBING_NAME.test(name)) return true;
  return mapped.has(name);
}

/**
 * The mapped-bone set the vocabulary consults. The rig carries its own
 * mapping (RigDescriptor.mapping, kernel-gated and post-graft), so the
 * evidence never has to be re-derived or passed around separately.
 */
export function mappedRigBones(rig: {
  readonly mapping: readonly { readonly rigBone: string }[];
}): ReadonlySet<string> {
  return new Set(rig.mapping.map((m) => m.rigBone));
}
